// Route -> dynamic-import factory. Used by Sidebar's hover prefetch to warm
// the lazy chunk before the user clicks. Same specifiers as the lazy() calls
// in App.tsx, so Vite dedupes the module graph and the chunk is fetched once.

type Loader = () => Promise<unknown>

const exactLoaders: Record<string, Loader> = {
  '/': () => import('@/pages/Landing'),
  '/login': () => import('@/pages/Login'),
  '/register': () => import('@/pages/Register'),
  '/forgot-password': () => import('@/pages/ForgotPassword'),
  '/reset-password': () => import('@/pages/ResetPassword'),
  '/verify-email': () => import('@/pages/VerifyEmail'),
  '/dashboard': () => import('@/pages/Dashboard'),
  '/screen': () => import('@/pages/Screen'),
  '/compare': () => import('@/pages/Compare'),
  '/manual-input': () => import('@/pages/ManualInput'),
  '/mna': () => import('@/pages/MNA'),
  '/analytics': () => import('@/pages/Analytics'),
  '/watchlist': () => import('@/pages/Watchlist'),
  '/events': () => import('@/pages/Events'),
  '/portfolio': () => import('@/pages/Portfolio'),
  '/estimates': () => import('@/pages/Estimates'),
  '/compliance': () => import('@/pages/Compliance'),
  '/devtools': () => import('@/pages/DevTools'),
  '/backtesting': () => import('@/pages/Backtesting'),
  '/batch': () => import('@/pages/BatchAnalysis'),
  '/factors': () => import('@/pages/FactorAnalytics'),
  '/ml-insights': () => import('@/pages/MLInsights'),
  '/strategies': () => import('@/pages/Strategies'),
  '/moat': () => import('@/pages/Moat'),
  '/credit-universe': () => import('@/pages/CreditUniverse'),
  '/crowded-trades': () => import('@/pages/CrowdedTrades'),
  '/calibration': () => import('@/pages/Calibration'),
  '/apophis': () => import('@/pages/apophis/ApophisCatalog'),
  '/apophis/documents': () => import('@/pages/apophis/ApophisDocuments'),
  '/apophis/history': () => import('@/pages/apophis/ApophisHistory'),
  '/profile': () => import('@/pages/Profile'),
  '/settings': () => import('@/pages/Settings'),
  '/terminal': () => import('@/terminal/Terminal'),
  '/contrarian': () => import('@/pages/ContrarianSignals'),
  '/thematic': () => import('@/pages/ThematicResearch'),
  '/podcasts': () => import('@/pages/PodcastIntelligence'),
  '/sector-skills': () => import('@/pages/SectorSkills'),
  '/research-inbox': () => import('@/pages/ResearchInbox'),
  '/peer-groups': () => import('@/pages/PeerGroups'),
  '/data-pipelines': () => import('@/pages/DataPipelines'),
}

const prefixLoaders: Array<[string, Loader]> = [
  ['/analysis/', () => import('@/pages/analysis/AnalysisShell')],
  ['/dashboard/', () => import('@/pages/dashboard/QuantitativePage')],
  ['/mna/results/', () => import('@/pages/MNAResults')],
  ['/jobs/', () => import('@/pages/JobProgress')],
  ['/moat/', () => import('@/pages/MoatDetail')],
  ['/apophis/new/', () => import('@/pages/apophis/ApophisGenerate')],
  ['/apophis/deliveries/', () => import('@/pages/apophis/ApophisDelivery')],
]

const inflight = new Set<Loader>()

export function prefetchRouteChunk(route: string): void {
  const loader =
    exactLoaders[route] ??
    prefixLoaders.find(([prefix]) => route.startsWith(prefix))?.[1]
  if (!loader || inflight.has(loader)) return
  inflight.add(loader)
  loader().catch(() => undefined)
}
